<template>
    <app-dialog
        :value="value"
        size="sm"
        append-to-body
        @input="$emit('input', $event)"
        @close="reset"
    >
        <template #header>
            <h3 class="text-lg font-semibold text-theme-base">
                ¿Cambiar a {{ targetLabel }}?
            </h3>
        </template>

        <div class="flex flex-col gap-3 text-sm">
            <p class="text-theme-muted">{{ targetDescription }}</p>

            <p
                v-if="turnsOnInventory"
                class="rounded-lg bg-theme-warning px-3 py-2 text-theme-inverse"
            >
                Tus productos dejan de estar siempre disponibles: lo que tenga
                stock 0 se verá agotado en tu catálogo hasta que cargues
                unidades.
            </p>

            <p
                v-if="targetMode === modes.none"
                class="rounded-lg bg-theme-hover px-3 py-2 text-theme-base"
            >
                Tus productos quedan siempre disponibles y las ventas dejan de
                descontar unidades.
            </p>

            <div
                v-if="targetMode === modes.merchant"
                class="flex flex-col gap-2"
            >
                <custom-checkbox v-model="moveToStore" primary>
                    Pasar el stock actual de cada producto a una sucursal
                </custom-checkbox>
                <custom-simple-select
                    v-if="moveToStore"
                    v-model="storeId"
                    label="Sucursal"
                    :options="storeOptions"
                    :error="storeError"
                />
                <p class="text-xs text-theme-muted">
                    Si no lo haces, el stock de cada producto será la suma de lo
                    que tengan tus sucursales (0 si aún no tienen inventario).
                </p>
            </div>

            <div v-if="leavesMerchant" class="flex flex-col gap-2">
                <custom-checkbox v-model="syncFromStores" primary>
                    Conservar el stock: dejar en cada producto la suma de sus
                    sucursales
                </custom-checkbox>
                <p
                    v-if="targetMode === modes.global && !syncFromStores"
                    class="text-xs text-theme-warning"
                >
                    Sin esta opción, el stock de cada producto queda en 0.
                </p>
            </div>

            <p
                v-if="targetMode === modes.merchant || leavesMerchant"
                class="text-xs text-theme-muted"
            >
                El inventario de cada sucursal no se borra.
            </p>
        </div>

        <template #footer>
            <div class="flex items-center gap-2">
                <app-button
                    class="flex-1"
                    color="success"
                    :loading="isSaving"
                    @click="confirm"
                >
                    Cambiar
                </app-button>
                <button
                    type="button"
                    :disabled="isSaving"
                    class="rounded-lg border border-theme-border bg-theme-card px-3 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover disabled:opacity-50"
                    @click="$emit('input', false)"
                >
                    Cancelar
                </button>
            </div>
        </template>
    </app-dialog>
</template>

<script>
import {
    INVENTORY_MODES,
    buildInventoryModePayload,
    findInventoryModeOption
} from "@/common/fields/inventory-modes";

import AppDialog from "@/components/utils/AppDialog.vue";
import CustomSimpleSelect from "@/components/utils/CustomSimpleSelect.vue";

export default {
    name: "InventoryModeDialog",
    components: { AppDialog, CustomSimpleSelect },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        currentMode: {
            type: String,
            default: INVENTORY_MODES.none
        },
        targetMode: {
            type: String,
            default: null
        },
        stores: {
            type: Array,
            default: () => []
        },
        isSaving: {
            type: Boolean,
            default: false
        }
    },
    emits: ["input", "confirm"],
    data: () => ({
        moveToStore: false,
        storeId: "",
        syncFromStores: true,
        storeError: ""
    }),
    computed: {
        modes() {
            return INVENTORY_MODES;
        },
        targetOption() {
            return findInventoryModeOption(this.targetMode);
        },
        targetLabel() {
            return this.targetOption
                ? this.targetOption.label.toLowerCase()
                : "";
        },
        targetDescription() {
            return this.targetOption ? this.targetOption.description : "";
        },
        turnsOnInventory() {
            return (
                this.currentMode === INVENTORY_MODES.none &&
                this.targetMode !== INVENTORY_MODES.none
            );
        },
        leavesMerchant() {
            return (
                this.currentMode === INVENTORY_MODES.merchant &&
                this.targetMode !== INVENTORY_MODES.merchant
            );
        },
        storeOptions() {
            return this.stores.map((store) => ({
                value: store.id,
                label: store.name
            }));
        }
    },
    methods: {
        reset() {
            this.moveToStore = false;
            this.storeId = "";
            this.syncFromStores = true;
            this.storeError = "";
        },
        confirm() {
            if (
                this.targetMode === INVENTORY_MODES.merchant &&
                this.moveToStore &&
                !this.storeId
            ) {
                this.storeError = "Elige la sucursal";
                return;
            }
            this.storeError = "";
            this.$emit(
                "confirm",
                buildInventoryModePayload({
                    currentMode: this.currentMode,
                    targetMode: this.targetMode,
                    storeId: this.moveToStore ? this.storeId : null,
                    syncFromStores: this.syncFromStores
                })
            );
        }
    }
};
</script>
